import React, { useEffect, useState } from 'react'
import { Button, Icon } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import type { IColumnsHeadersOverwrite } from '@src/interfaces'
import type { RootState } from '@src/store'
import { goTo, updateTemplate } from '@src/slices/configuration.slice'
import { Paths } from '@src/enums/paths'
import { changeTemplateState, deleteTemplate, getCustomTemplates, getTemplates } from '@src/services/config.service'
import { notificationAlert } from '@src/modules/atoms/notification'
import { currentUserLang } from '@src/helpers/current-user-lang'
import ConfirmationActionModal from '@src/modules/molecules/confirmation-action-modal'
import { TemplatesTable } from '@src/components/TemplatesTable'
import { headers } from './headers'
import styles from './styles.module.scss'

const lang = currentUserLang()

interface IMain {
  onEditTemplate?: (template: any) => void
  overwriteColumns?: IColumnsHeadersOverwrite[]
  permissions: any
  url?: string
  onDuplicate?: (token: string) => void
}

interface IOption {
  title: string;
  onClick: () => void;
  disabled: boolean
}

const Main: React.FC<IMain> = ({
  onEditTemplate,
  overwriteColumns = [],
  permissions = { create: true, update: true, destroy: true },
  url,
  onDuplicate
}) => {
  const dispatch = useDispatch()
  const baseProps = useSelector(({ baseProps }: RootState) => baseProps)
  const [templates, setTemplates] = useState([])
  const [alert, setAlert] = useState<React.ReactNode>(<div />)

  const onChangeStateClick = (template: any) => {
    const { token: templateToken } = template
    changeTemplateState(baseProps, templateToken, lang).then(({ success }) => {
      if (success) {
        fetchTemplates()
        notificationAlert({
          key: 'changeStateTemplateNotification',
          message: 'Plantilla actualizada',
          description: 'La plantilla ha sido actualizada correctamente',
          variant: 'success'
        })
      }
    })
  }

  const onEditClick = (template: any) => {
    dispatch(goTo(Paths.editTemplate))
    dispatch(updateTemplate({ prop: 'token', value: template.token, isNew: false }))
  }

  const onDeleteClick = (template: any) => {
    const closeModal = () => {
      setAlert(<div />)
    }
    const { token: templateToken } = template
    setAlert(
      <ConfirmationActionModal
        title='¿Estas seguro de eliminar la plantilla?'
        content={
          <p>
            Una vez se confirme esta acción la plantilla será eliminada y no podrá ser recuperada.
          </p>
        }
        onOk={() => {
          onConfirmDeleteTemplate(templateToken)
          closeModal()
        }}
        onCancel={closeModal}
      />
    )
  }

  const onConfirmDeleteTemplate = (templateToken: string) => {
    deleteTemplate(baseProps, templateToken, lang).then(({ success }) => {
      if (success) {
        fetchTemplates()
        notificationAlert({
          key: 'deleteTemplateNotification',
          message: 'Plantilla eliminada',
          description: 'La plantilla ha sido eliminda correctamente',
          variant: 'success'
        })
      }
    })
  }

  const buildItems = (template: any) => {
    const items: IOption[] = []

    if (permissions.update) {
      items.push(
        {
          title: template.active ? 'Desactivar' : 'Activar',
          onClick: () => onChangeStateClick(template),
          disabled: !permissions.update
        }
      )
      items.push(
        {
          title: 'Editar',
          onClick: () => {
            onEditTemplate?.(template)
            onEditClick(template)
          },
          disabled: !permissions.update
        }
      )
    }
    if (onDuplicate) {
      items.push(
        {
          title: 'Duplicar',
          onClick: () => onDuplicate(template.token),
          disabled: false
        }
      )
    }
    if (permissions.destroy) {
      items.push(
        {
          title: 'Eliminar',
          onClick: () => onDeleteClick(template),
          disabled: !permissions.destroy
        }
      )
    }

    return items
  }

  const fetchTemplates = () => {
    if(url) {
      getCustomTemplates(url).then((data) => {
        setTemplates(data)
      })
    } else {
      getTemplates(baseProps, lang).then(({ data }) => {
        const templates: any = Object.values(data).flat()
        setTemplates(templates)
      })
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [baseProps.resource])

  const columnHeaders = headers(buildItems, permissions)

  return (
    <div>
      {alert}
      <div className={styles.header}>
        <h1 className={styles.title}>Plantillas</h1>
        {permissions?.create && (
          <Button
            className={styles.createButton}
            type='primary'
            onClick={() => {
              dispatch(goTo(Paths.newTemplate))
            }}
          >
            <Icon type='plus' /> Nueva plantilla
          </Button>
        )}
      </div>
      <TemplatesTable
        templates={templates}
        columnHeaders={columnHeaders}
        overwriteColumns={overwriteColumns}
      />
    </div>
  )
}

export default Main
